// import 'aok-assets/dist/base/base-sg.css'
import DOMPurify from 'dompurify'
import React, { createRef, useContext } from 'react'
import { routes } from './routes/routes'
import { TextContext, TextContextConsumer } from 'hocs'
import { Route, Switch } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  hideBookmarkOverlay,
  toggleBookmark,
} from './redux/actions/bookmarkActions'
import { LocalisationProvider, useMobileSwitch } from './hooks'

// components && styling
import 'assets/aok-assets/dist_base_base-sg.css'
import Overlay from './components/molecules/Overlay/Overlay'
import { Button, BuildInfo } from './components/atoms'
import { HeaderProvider, WindowCurrentUriSetter } from './hocs/'
import { entryPointId } from './settings/app/constants'
import { getAdditionalSearchbarSelectOptionLabel } from './settings/app/helpers'
import { bookmarksSearchContextHasChanged } from './containers/Bookmarks/helpers/helpers'

/**
 * client routing and overlay handling
 * @returns {JSX.Element}
 * @constructor
 */
const Application = () => {
  const dispatch = useDispatch()
  const appReference = createRef()
  const isMobile = useMobileSwitch()
  const textContext = useContext(TextContext)
  const { bookmarks } = useSelector((state) => state)

  const getBookmarkHint = (labels) => {
    const copy = labels['bookmark.warning.copy']
    const searchForAll = labels['bookmark.warning.searchForAll']

    // if a bookmark element exists with search params
    if (bookmarks.overlay.bookmarkToAdd) {
      const { type, searchParams: newBookmarkParams } =
        bookmarks.overlay.bookmarkToAdd
      let newCopy = labels[`bookmark.warning.copy.${ type }`]

      /**
       * get old and new search term for overlay message
       */
      let oldSearchTerm = ''
      let newBookmarkSearchTerm = ''
      switch (type) {
        case entryPointId.HOSPITALS:
          // current searchTerm (from elements which are booked before new booking)
          oldSearchTerm = bookmarks.types[type].searchParams.query
          oldSearchTerm =
            oldSearchTerm === '' || typeof oldSearchTerm === 'undefined'
              ? searchForAll
              : oldSearchTerm

          // new booking element
          newBookmarkSearchTerm =
            newBookmarkParams.query === '' ||
            typeof newBookmarkParams.query === 'undefined'
              ? searchForAll
              : newBookmarkParams.query
          break

        // consider only one param
        case entryPointId.CARESERVICES:
          oldSearchTerm = getAdditionalSearchbarSelectOptionLabel({
            resultType: type,
            selectTyp: 'PE_ANG_KRANKENPFLEGE',
            selectValue:
              bookmarks.types[type].searchParams.PE_ANG_KRANKENPFLEGE,
          })
          newBookmarkSearchTerm = getAdditionalSearchbarSelectOptionLabel({
            resultType: type,
            selectTyp: 'PE_ANG_KRANKENPFLEGE',
            selectValue: newBookmarkParams.PE_ANG_KRANKENPFLEGE,
          })
          break

        // consider only two param
        case entryPointId.DAYANDNIGHTCARE:
        case entryPointId.CAREHOMES:
          const { searchContextDiff } = bookmarksSearchContextHasChanged({
            newBookmark: bookmarks.overlay.bookmarkToAdd,
            bookmarkState: bookmarks.types[type],
          })
          newCopy =
            textContext[`bookmark.warning.copy.${ type }.${ searchContextDiff }`]

          // booth params are different
          // if (searchContextDiff.length > 1) {}

          if (searchContextDiff.length === 1) {
            const contextKey = searchContextDiff

            oldSearchTerm = getAdditionalSearchbarSelectOptionLabel({
              resultType: type,
              selectTyp: contextKey[0],
              selectValue: bookmarks.types[type].searchParams[contextKey],
            })
            newBookmarkSearchTerm = getAdditionalSearchbarSelectOptionLabel({
              resultType: type,
              selectTyp: contextKey[0],
              selectValue: newBookmarkParams[contextKey],
            })
          }
          break
      }

      return newCopy
        .replace('__OLD_SEARCH__', oldSearchTerm)
        .replace('__CURRENT_SEARCH__', newBookmarkSearchTerm)
    }

    return copy
  }

  return (
    <WindowCurrentUriSetter>
      <LocalisationProvider>
        <HeaderProvider>
          <div
            ref={ appReference }
            className='container-fluid app-main'
            data-cy-mobile-device={ isMobile }>
            <BuildInfo />
            <Switch>
              {routes.map((route, index) => (
                <Route
                  key={ index }
                  path={ route.path }
                  render={ () => <route.component /> }
                />
              ))}
            </Switch>
          </div>

          <TextContextConsumer>
            {(labels) => (
              <Overlay
                id={ 'bookmark-overlay' }
                isActive={ bookmarks.overlay.show }
                title={ labels['bookmark.warning.title'] }
                showClose={ false }
                iconName={ 'icon-f-info-circle-full' }>
                <p
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(getBookmarkHint(labels)),
                  }}
                />
                <div className={ 'bookmark-btn-container' }>
                  <Button
                    text={ labels['bookmark.warning.abort'] }
                    className={ 'with-border bookmark-overlay-btn-cancel' }
                    onClick={ () => dispatch(hideBookmarkOverlay()) }
                    data-cy='cancel-bookmarks-delete'
                  />
                  <Button
                    text={ labels['bookmark.warning.save'] }
                    data-cy='delete-current-bookmarks'
                    className={ 'btn-default bookmark-overlay-btn-save' }
                    onClick={ () => {
                      if (!bookmarks.overlay.bookmarkToAdd) return
                      dispatch(
                        toggleBookmark({
                          id: bookmarks.overlay.bookmarkToAdd.id,
                          type: bookmarks.overlay.bookmarkToAdd.type,
                          searchParams: {
                            ...bookmarks.overlay.bookmarkToAdd.searchParams,
                          },
                          reset: true,
                        })
                      )
                    } }
                  />
                </div>
              </Overlay>
            )}
          </TextContextConsumer>
        </HeaderProvider>
      </LocalisationProvider>
    </WindowCurrentUriSetter>
  )
}

export default Application
